import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { bankingService } from '../../../services/bankingService';
import ManualTransactionForm from '../../banking/components/ManualTransactionForm';
import Icon from '../../../components/AppIcon';

const ManualTransactionSettings = () => {
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBankAccounts();
    loadRecentTransactions();
  }, []);

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      const accounts = await bankingService?.getAllBankAccounts();
      setBankAccounts(accounts);
    } catch (err) {
      console.error('Error loading bank accounts:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentTransactions = async () => {
    try {
      const transactions = await bankingService?.getBankAccountTransactions('all', {
        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0] // Last 30 days
      });
      setRecentTransactions(transactions?.slice(0, 5)); // Get last 5 transactions
    } catch (err) {
      console.error('Error loading recent transactions:', err);
    }
  };

  const handleCreateTransaction = async (transactionData) => {
    try {
      setLoading(true);
      await bankingService?.createTransaction({
        bankAccountId: transactionData?.account,
        transactionDate: transactionData?.date,
        description: transactionData?.description,
        amount: transactionData?.amount,
        category: transactionData?.category,
        referenceNumber: transactionData?.reference,
        reconciliationStatus: 'unreconciled'
      });
      
      // Reload recent transactions
      await loadRecentTransactions();
      setError(null);
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={16} className="text-error" />
          <p className="text-error font-medium">Error</p>
        </div>
        <p className="text-error/80 text-sm mt-1">{error}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setError(null);
            loadBankAccounts();
            loadRecentTransactions();
          }}
          className="mt-2"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Manual Transactions</h2>
          <p className="text-muted-foreground mt-1">
            Add transactions that don't appear in your bank feed automatically
          </p>
        </div>
        <Button
          onClick={() => setShowTransactionForm(true)}
          iconName="Plus"
          iconPosition="left"
          disabled={loading || bankAccounts?.length === 0}
        >
          Add Manual Transaction
        </Button>
      </div>
      {/* Bank Accounts Status */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Connected Accounts</h3>
        
        {loading ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">Loading accounts...</span>
          </div>
        ) : bankAccounts?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bankAccounts?.map((account) => (
              <div key={account?.id} className="bg-background border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-foreground">{account?.accountName}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    account?.status === 'active' ?'bg-success/10 text-success border border-success/20' :'bg-muted text-muted-foreground'
                  }`}>
                    {account?.status}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {account?.bankName} • ...{account?.accountNumber?.slice(-4)}
                </p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(account?.currentBalance)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Bank Accounts</h4>
            <p className="text-muted-foreground mb-4">
              You need to set up at least one bank account before adding manual transactions.
            </p>
            <Button variant="outline" iconName="Plus" iconPosition="left">
              Add Bank Account
            </Button>
          </div>
        )}
      </div>
      {/* Recent Manual Transactions */}
      {recentTransactions?.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Recent Manual Transactions</h3>
            <Button variant="ghost" size="sm" iconName="ExternalLink">
              View All
            </Button>
          </div>
          
          <div className="space-y-3">
            {recentTransactions?.map((transaction) => (
              <div key={transaction?.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${
                    transaction?.reconciliationStatus === 'reconciled' ?'bg-success' 
                      : transaction?.reconciliationStatus === 'pending' ?'bg-warning' :'bg-muted'
                  }`} />
                  <div>
                    <p className="font-medium text-foreground">{transaction?.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {transaction?.category} • {formatDate(transaction?.transactionDate)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                    transaction?.amount > 0 ? 'text-success' : 'text-error'
                  }`}>
                    {transaction?.amount > 0 ? '+' : ''}{formatCurrency(transaction?.amount)}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {transaction?.reconciliationStatus}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Help Section */}
      <div className="bg-muted/20 border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-2">About Manual Transactions</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Use manual transactions to record financial activity that doesn't automatically 
                appear in your bank feeds, such as:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Cash payments and receipts</li>
                <li>Bank fees not yet reflected</li>
                <li>Adjustments and corrections</li>
                <li>Pending transactions</li>
              </ul>
              <p className="mt-3">
                All manual transactions are marked as "unreconciled" by default and can be 
                reconciled during your regular bank reconciliation process.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Manual Transaction Form Modal */}
      <ManualTransactionForm
        isOpen={showTransactionForm}
        onClose={() => setShowTransactionForm(false)}
        onSubmit={handleCreateTransaction}
        bankAccounts={bankAccounts}
      />
    </div>
  );
};

export default ManualTransactionSettings;